import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Minus, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@nokowazzi/api-client';
import type { TradeDirection } from '@nokowazzi/types';
import { createRequestSchema, CURRENCY_SYMBOL } from '@nokowazzi/types';
import {
  Button,
  FormField,
  IconButton,
  Input,
  Select,
  SegmentedControl,
  Textarea,
  toast,
  TopAppBar,
} from '@nokowazzi/ui';
import { requireAuthBeforeLoad } from '../features/auth/guard';
import { useCategories, useCreateRequest } from '../features/sourcing/api';
import { categoryLabel, originCurrency } from '../features/sourcing/format';
import { ImageUploader } from '../features/sourcing/ImageUploader';
import { guessViewerCountry, useMyProfile } from '../features/sourcing/viewer';

export const Route = createFileRoute('/sourcing/new')({
  beforeLoad: () => requireAuthBeforeLoad('/sourcing/new'),
  component: NewRequestScreen,
});

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 999;

interface FieldErrors {
  title?: string;
  budget?: string;
}

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

function defaultDirectionFor(country: 'KR' | 'JP'): TradeDirection {
  return country === 'KR' ? 'JP_TO_KR' : 'KR_TO_JP';
}

function NewRequestScreen() {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();

  const { data: profile } = useMyProfile();
  const categoriesQuery = useCategories();
  const createRequest = useCreateRequest();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [direction, setDirection] = useState<TradeDirection>(() =>
    defaultDirectionFor(guessViewerCountry()),
  );
  const directionTouched = useRef(false);
  // 항상 문자열로 유지해 Select이 controlled 상태를 벗어나지 않게 한다('' = 미선택)
  const [categoryId, setCategoryId] = useState<string>('');
  const [budget, setBudget] = useState('');
  const [quantity, setQuantity] = useState(MIN_QUANTITY);
  const [city, setCity] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [imagesUploading, setImagesUploading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  // 프로필 로드 후, 사용자가 직접 바꾸기 전까지 기본 방향을 프로필 국가 기준으로 맞춘다
  useEffect(() => {
    if (profile !== undefined && !directionTouched.current) {
      setDirection(defaultDirectionFor(profile.country));
    }
  }, [profile]);

  const currency = originCurrency(direction);
  const locale = i18n.language;
  const categoryOptions = useMemo(
    () =>
      (categoriesQuery.data ?? []).map((category) => ({
        value: category.id,
        label: categoryLabel(category.name_i18n, locale),
      })),
    [categoriesQuery.data, locale],
  );

  const handleBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/sourcing' });
    }
  };

  const handleSubmit = () => {
    const parsed = createRequestSchema.safeParse({
      direction,
      categoryId: categoryId === '' ? undefined : categoryId,
      title: title.trim(),
      description: description.trim() === '' ? undefined : description.trim(),
      city: city.trim() === '' ? undefined : city.trim(),
      budgetAmount: budget === '' ? undefined : Number(budget),
      budgetCurrency: currency,
      quantity,
      images,
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === 'title') next.title = t('sourcing.form.titleInvalid');
        if (field === 'budgetAmount') next.budget = t('sourcing.form.budgetInvalid');
      }
      setErrors(next);
      if (next.title === undefined && next.budget === undefined) {
        toast.show({ type: 'error', message: t('errors.generic') });
      }
      return;
    }

    setErrors({});
    createRequest.mutate(parsed.data, {
      onSuccess: (data) => {
        toast.show({ type: 'success', message: t('sourcing.form.created') });
        void navigate({
          to: '/sourcing/$requestId',
          params: { requestId: data.request.id },
          replace: true,
        });
      },
      onError: (error) => {
        toast.show({
          type: 'error',
          message: error instanceof ApiError && error.status === 429
            ? t('errors.rateLimited')
            : t('errors.generic'),
        });
      },
    });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <TopAppBar
        title={t('sourcing.newRequest')}
        leading={
          <IconButton aria-label={t('actions.back')} onClick={handleBack}>
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        }
      />

      <main className="flex flex-1 flex-col gap-5 px-5 pt-2 pb-[calc(var(--safe-bottom)+104px)]">
        <Input
          label={t('sourcing.form.titleLabel')}
          placeholder={t('sourcing.form.titlePlaceholder')}
          value={title}
          maxLength={120}
          showCount
          onChange={(event) => setTitle(event.target.value)}
          error={errors.title}
        />

        <Textarea
          label={t('sourcing.form.descriptionLabel')}
          placeholder={t('sourcing.form.descriptionPlaceholder')}
          rows={5}
          maxLength={4000}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        <FormField label={t('sourcing.form.directionLabel')}>
          <SegmentedControl<TradeDirection>
            options={[
              { value: 'JP_TO_KR', label: t('direction.jpToKr') },
              { value: 'KR_TO_JP', label: t('direction.krToJp') },
            ]}
            value={direction}
            onChange={(value) => {
              directionTouched.current = true;
              setDirection(value);
            }}
          />
        </FormField>

        <Select
          label={t('sourcing.form.categoryLabel')}
          placeholder={t('sourcing.form.categoryPlaceholder')}
          options={categoryOptions}
          value={categoryId}
          onValueChange={setCategoryId}
        />

        <Input
          label={t('sourcing.budget')}
          inputMode="numeric"
          autoComplete="off"
          value={budget}
          onChange={(event) => setBudget(digitsOnly(event.target.value))}
          prefix={<span className="text-body font-medium">{CURRENCY_SYMBOL[currency]}</span>}
          suffix={<span className="text-caption1">{currency}</span>}
          error={errors.budget}
        />

        <FormField label={t('sourcing.quantity')}>
          <div className="flex items-center gap-4">
            <IconButton
              variant="subtle"
              aria-label={t('sourcing.form.quantityDecrease')}
              disabled={quantity <= MIN_QUANTITY}
              onClick={() => setQuantity((value) => Math.max(MIN_QUANTITY, value - 1))}
            >
              <Minus aria-hidden="true" />
            </IconButton>
            <span className="min-w-10 text-center text-title3 text-fg tabular-nums">
              {quantity}
            </span>
            <IconButton
              variant="subtle"
              aria-label={t('sourcing.form.quantityIncrease')}
              disabled={quantity >= MAX_QUANTITY}
              onClick={() => setQuantity((value) => Math.min(MAX_QUANTITY, value + 1))}
            >
              <Plus aria-hidden="true" />
            </IconButton>
          </div>
        </FormField>

        <Input
          label={t('sourcing.city')}
          placeholder={t('sourcing.form.cityPlaceholder')}
          maxLength={80}
          value={city}
          onChange={(event) => setCity(event.target.value)}
        />

        <FormField label={t('sourcing.form.imagesLabel')}>
          <ImageUploader
            paths={images}
            onChange={setImages}
            onUploadingChange={setImagesUploading}
            disabled={createRequest.isPending}
          />
        </FormField>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-page px-5 pt-3 pb-[calc(var(--safe-bottom)+12px)]">
        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={createRequest.isPending}
          disabled={imagesUploading}
          onClick={handleSubmit}
        >
          {t('actions.submit')}
        </Button>
      </div>
    </div>
  );
}
